import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

interface ProductData {
  name: string;
  slug: string;
  coverageType: string;
  premiumCost: string;
  cashValue: { text: string; status: 'positive' | 'neutral' | 'negative' };
  medicalExam: { text: string; status: 'positive' | 'neutral' | 'negative' };
  bestFor: string;
  policyLength: string;
  deathBenefit: string;
  taxAdvantages: { text: string; status: 'positive' | 'neutral' | 'negative' };
  flexibility: { text: string; status: 'positive' | 'neutral' | 'negative' };
  idealAgeRange: string;
}

const products: Record<string, ProductData> = {
  'term-life': {
    name: 'Term Life Insurance',
    slug: '/term-life',
    coverageType: 'Temporary Coverage',
    premiumCost: '$20-$50/month',
    cashValue: { text: 'No Cash Value', status: 'negative' },
    medicalExam: { text: 'Usually Required', status: 'neutral' },
    bestFor: 'Young families, mortgage protection, budget-conscious buyers',
    policyLength: '10, 20, or 30 years',
    deathBenefit: 'Fixed amount, no growth',
    taxAdvantages: { text: 'Tax-free death benefit only', status: 'neutral' },
    flexibility: { text: 'Limited - expires at term end', status: 'negative' },
    idealAgeRange: '25-50 years old',
  },
  'whole-life': {
    name: 'Whole Life Insurance',
    slug: '/whole-life',
    coverageType: 'Permanent Coverage',
    premiumCost: '$100-$300/month',
    cashValue: { text: 'Guaranteed Growth', status: 'positive' },
    medicalExam: { text: 'Usually Required', status: 'neutral' },
    bestFor: 'Estate planning, lifelong coverage, wealth transfer',
    policyLength: 'Lifetime coverage',
    deathBenefit: 'Guaranteed + dividends',
    taxAdvantages: { text: 'Tax-deferred growth + tax-free loans', status: 'positive' },
    flexibility: { text: 'Fixed premiums, predictable', status: 'neutral' },
    idealAgeRange: '30-60 years old',
  },
  'iul': {
    name: 'Indexed Universal Life',
    slug: '/indexed-universal-life',
    coverageType: 'Permanent Coverage',
    premiumCost: '$150-$400/month',
    cashValue: { text: 'Market-indexed growth potential', status: 'positive' },
    medicalExam: { text: 'Usually Required', status: 'neutral' },
    bestFor: 'Wealth building, retirement income, market upside with downside protection',
    policyLength: 'Lifetime coverage',
    deathBenefit: 'Flexible + cash value growth',
    taxAdvantages: { text: 'Tax-free growth + retirement income', status: 'positive' },
    flexibility: { text: 'Highly flexible premiums & benefits', status: 'positive' },
    idealAgeRange: '35-55 years old',
  },
  'universal-life': {
    name: 'Universal Life Insurance',
    slug: '/universal-life',
    coverageType: 'Permanent Coverage',
    premiumCost: '$80-$250/month',
    cashValue: { text: 'Fixed interest rate growth', status: 'neutral' },
    medicalExam: { text: 'Usually Required', status: 'neutral' },
    bestFor: 'Flexible coverage needs, adjustable premiums',
    policyLength: 'Lifetime coverage',
    deathBenefit: 'Adjustable death benefit',
    taxAdvantages: { text: 'Tax-deferred growth', status: 'neutral' },
    flexibility: { text: 'Very flexible premiums & coverage', status: 'positive' },
    idealAgeRange: '30-60 years old',
  },
  'final-expense': {
    name: 'Final Expense Insurance',
    slug: '/final-expense',
    coverageType: 'Permanent Coverage',
    premiumCost: '$30-$100/month',
    cashValue: { text: 'Minimal or none', status: 'negative' },
    medicalExam: { text: 'No Exam Required', status: 'positive' },
    bestFor: 'Seniors, covering funeral costs, simplified underwriting',
    policyLength: 'Lifetime coverage',
    deathBenefit: '$5,000-$25,000 fixed',
    taxAdvantages: { text: 'Tax-free death benefit', status: 'neutral' },
    flexibility: { text: 'Fixed premiums, simple', status: 'neutral' },
    idealAgeRange: '50-85 years old',
  },
  'mortgage-protection': {
    name: 'Mortgage Protection',
    slug: '/mortgage-protection',
    coverageType: 'Decreasing Term Coverage',
    premiumCost: '$25-$80/month',
    cashValue: { text: 'No Cash Value', status: 'negative' },
    medicalExam: { text: 'Often Simplified', status: 'positive' },
    bestFor: 'Homeowners, protecting mortgage balance',
    policyLength: 'Matches mortgage term (15-30 years)',
    deathBenefit: 'Decreases with mortgage balance',
    taxAdvantages: { text: 'Tax-free death benefit', status: 'neutral' },
    flexibility: { text: 'Limited - tied to mortgage', status: 'negative' },
    idealAgeRange: '25-55 years old',
  },
  'annuities': {
    name: 'Annuities',
    slug: '/annuities',
    coverageType: 'Retirement Income Product',
    premiumCost: 'Lump sum or periodic payments',
    cashValue: { text: 'Accumulation phase growth', status: 'positive' },
    medicalExam: { text: 'No Exam Required', status: 'positive' },
    bestFor: 'Retirement income, guaranteed lifetime payments',
    policyLength: 'Lifetime income stream',
    deathBenefit: 'Optional death benefit rider',
    taxAdvantages: { text: 'Tax-deferred growth', status: 'positive' },
    flexibility: { text: 'Various payout options', status: 'positive' },
    idealAgeRange: '50-75 years old',
  },
  'no-medical-exam': {
    name: 'No Medical Exam Life',
    slug: '/no-medical-exam',
    coverageType: 'Term or Permanent',
    premiumCost: '$40-$150/month',
    cashValue: { text: 'Depends on policy type', status: 'neutral' },
    medicalExam: { text: 'No Exam Required', status: 'positive' },
    bestFor: 'Quick approval, health concerns, convenience',
    policyLength: 'Varies by policy type',
    deathBenefit: 'Up to $500,000',
    taxAdvantages: { text: 'Standard tax benefits', status: 'neutral' },
    flexibility: { text: 'Fast approval process', status: 'positive' },
    idealAgeRange: '25-65 years old',
  },
};

const presetComparisons = [
  { label: 'Term vs Whole Life', productA: 'term-life', productB: 'whole-life' },
  { label: 'IUL vs Universal Life', productA: 'iul', productB: 'universal-life' },
  { label: 'Final Expense vs Whole Life', productA: 'final-expense', productB: 'whole-life' },
  { label: 'Term vs Mortgage Protection', productA: 'term-life', productB: 'mortgage-protection' },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

const ComparisonPage = () => {
  const [productA, setProductA] = useState<string>('term-life');
  const [productB, setProductB] = useState<string>('whole-life');

  useEffect(() => {
    document.title = 'Compare Life Insurance Policies | Indianapolis';

    const metaTags: { name?: string; property?: string; content: string }[] = [
      { name: 'description', content: 'Compare life insurance policies side-by-side: term, whole life, IUL, final expense, annuities & more. Find the best coverage for Indianapolis families. Free expert guidance.' },
      { name: 'keywords', content: 'compare life insurance Indianapolis, life insurance comparison Indiana, term vs whole life Indianapolis, best life insurance policy Indiana' },
      { property: 'og:title', content: 'Compare Life Insurance Policies Indianapolis | Side-by-Side Coverage Guide' },
      { property: 'og:description', content: 'Compare life insurance policies side-by-side. Find the best coverage for Indianapolis families.' },
      { property: 'og:url', content: `${SITE_URL}/compare` },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ];

    const created: HTMLElement[] = [];

    metaTags.forEach((tag) => {
      const key = tag.name || tag.property || '';
      const attr = tag.name ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', tag.content);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/compare`);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Compare Life Insurance Policies Indianapolis',
        description: 'Compare life insurance policies side-by-side: term, whole life, IUL, final expense, annuities and more.',
        url: `${SITE_URL}/compare`,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Compare Policies', item: `${SITE_URL}/compare` },
        ],
      },
    ];

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.id = `schema-compare-${i}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      created.forEach((el) => { try { document.head.removeChild(el); } catch (e) { /* ignore */ } });
      scripts.forEach((s) => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } });
    };
  }, []);

  const dataA = products[productA];
  const dataB = products[productB];

  const handleSwap = () => {
    const temp = productA;
    setProductA(productB);
    setProductB(temp);
  };

  const handlePresetComparison = (presetA: string, presetB: string) => {
    setProductA(presetA);
    setProductB(presetB);
  };

  const getStatusColor = (status: 'positive' | 'neutral' | 'negative') => {
    if (status === 'positive') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'negative') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getStatusIcon = (status: 'positive' | 'neutral' | 'negative') => {
    if (status === 'positive') return <i className="ri-checkbox-circle-fill text-emerald-600"></i>;
    if (status === 'negative') return <i className="ri-information-fill text-amber-600"></i>;
    return <i className="ri-subtract-line text-slate-500"></i>;
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-50 via-white to-orange-50 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-teal-500 rounded-2xl mb-6">
                <i className="ri-scales-3-line text-3xl text-white"></i>
              </div>
              <h1 className="text-5xl font-bold text-slate-900 mb-4">
                Compare Insurance Products
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Make an informed decision by comparing coverage, costs, and benefits side-by-side
              </p>
            </div>

            {/* Quick Preset Comparisons */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <span className="text-sm font-medium text-slate-600 self-center">Popular Comparisons:</span>
              {presetComparisons.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetComparison(preset.productA, preset.productB)}
                  className="px-4 py-2 bg-white border-2 border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-all duration-200 whitespace-nowrap"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Product Selectors */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
              <div className="grid md:grid-cols-3 gap-6 items-center">
                {/* Product A Selector */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Product A
                  </label>
                  <select
                    value={productA}
                    onChange={(e) => setProductA(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:border-orange-500 focus:bg-white transition-all duration-200 cursor-pointer"
                  >
                    {Object.entries(products).map(([key, product]) => (
                      <option key={key} value={key}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Swap Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleSwap}
                    className="w-12 h-12 bg-gradient-to-br from-orange-500 to-teal-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-200 shadow-lg"
                    aria-label="Swap products"
                  >
                    <i className="ri-arrow-left-right-line text-xl"></i>
                  </button>
                </div>

                {/* Product B Selector */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Product B
                  </label>
                  <select
                    value={productB}
                    onChange={(e) => setProductB(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:border-[#1A3A52] focus:bg-white transition-all duration-200 cursor-pointer"
                  >
                    {Object.entries(products).map(([key, product]) => (
                      <option key={key} value={key}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
                      <th className="px-6 py-5 text-left text-sm font-bold text-slate-700 uppercase tracking-wider w-1/3">
                        Feature
                      </th>
                      <th className="px-6 py-5 text-center text-lg font-bold text-orange-600 border-l-2 border-orange-200 w-1/3">
                        {dataA.name}
                      </th>
                      <th className="px-6 py-5 text-center text-lg font-bold text-[#1A3A52] border-l-2 border-[#1A3A52]/20 w-1/3">
                        {dataB.name}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {/* Coverage Type */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-shield-check-line text-slate-500"></i>
                          Coverage Type
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="inline-block px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium border border-slate-200">
                          {dataA.coverageType}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="inline-block px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium border border-slate-200">
                          {dataB.coverageType}
                        </span>
                      </td>
                    </tr>

                    {/* Premium Cost */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-money-dollar-circle-line text-slate-500"></i>
                          Premium Cost Range
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-base font-bold text-slate-900">{dataA.premiumCost}</span>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-base font-bold text-slate-900">{dataB.premiumCost}</span>
                      </td>
                    </tr>

                    {/* Cash Value */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-line-chart-line text-slate-500"></i>
                          Cash Value
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataA.cashValue.status)}`}>
                          {getStatusIcon(dataA.cashValue.status)}
                          {dataA.cashValue.text}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataB.cashValue.status)}`}>
                          {getStatusIcon(dataB.cashValue.status)}
                          {dataB.cashValue.text}
                        </div>
                      </td>
                    </tr>

                    {/* Medical Exam */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-stethoscope-line text-slate-500"></i>
                          Medical Exam Required
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataA.medicalExam.status)}`}>
                          {getStatusIcon(dataA.medicalExam.status)}
                          {dataA.medicalExam.text}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataB.medicalExam.status)}`}>
                          {getStatusIcon(dataB.medicalExam.status)}
                          {dataB.medicalExam.text}
                        </div>
                      </td>
                    </tr>

                    {/* Best For */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-user-star-line text-slate-500"></i>
                          Best For
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <p className="text-sm text-slate-700 leading-relaxed">{dataA.bestFor}</p>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <p className="text-sm text-slate-700 leading-relaxed">{dataB.bestFor}</p>
                      </td>
                    </tr>

                    {/* Policy Length */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-calendar-line text-slate-500"></i>
                          Policy Length
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-sm font-medium text-slate-700">{dataA.policyLength}</span>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-sm font-medium text-slate-700">{dataB.policyLength}</span>
                      </td>
                    </tr>

                    {/* Death Benefit */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-heart-pulse-line text-slate-500"></i>
                          Death Benefit
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-sm font-medium text-slate-700">{dataA.deathBenefit}</span>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-sm font-medium text-slate-700">{dataB.deathBenefit}</span>
                      </td>
                    </tr>

                    {/* Tax Advantages */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-file-text-line text-slate-500"></i>
                          Tax Advantages
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataA.taxAdvantages.status)}`}>
                          {getStatusIcon(dataA.taxAdvantages.status)}
                          {dataA.taxAdvantages.text}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataB.taxAdvantages.status)}`}>
                          {getStatusIcon(dataB.taxAdvantages.status)}
                          {dataB.taxAdvantages.text}
                        </div>
                      </td>
                    </tr>

                    {/* Flexibility */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-settings-3-line text-slate-500"></i>
                          Flexibility
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataA.flexibility.status)}`}>
                          {getStatusIcon(dataA.flexibility.status)}
                          {dataA.flexibility.text}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(dataB.flexibility.status)}`}>
                          {getStatusIcon(dataB.flexibility.status)}
                          {dataB.flexibility.text}
                        </div>
                      </td>
                    </tr>

                    {/* Ideal Age Range */}
                    <tr className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-6 py-5 text-sm font-semibold text-slate-700">
                        <div className="flex items-center gap-2">
                          <i className="ri-user-line text-slate-500"></i>
                          Ideal Age Range
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-sm font-medium text-slate-700">{dataA.idealAgeRange}</span>
                      </td>
                      <td className="px-6 py-5 text-center border-l-2 border-slate-100">
                        <span className="text-sm font-medium text-slate-700">{dataB.idealAgeRange}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-slate-50 via-white to-orange-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">
                Ready to Get Started?
              </h2>
              <p className="text-lg text-slate-600">
                Learn more about each product or get a personalized quote
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Product A CTA */}
              <div className="bg-white rounded-2xl shadow-lg border-2 border-orange-200 p-8 hover:shadow-xl transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                    <i className="ri-file-list-3-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{dataA.name}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-6">
                  {dataA.bestFor}
                </p>
                <a
                  href={dataA.slug}
                  className="block w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-200 text-center whitespace-nowrap"
                >
                  Learn More About {dataA.name}
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>

              {/* Product B CTA */}
              <div className="bg-white rounded-2xl shadow-lg border-2 border-[#1A3A52]/20 p-8 hover:shadow-xl transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-xl flex items-center justify-center">
                    <i className="ri-file-list-3-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{dataB.name}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-6">
                  {dataB.bestFor}
                </p>
                <a
                  href={dataB.slug}
                  className="block w-full px-6 py-3 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] text-white font-semibold rounded-xl hover:from-[#0F2A3F] hover:to-[#0A1E2E] transition-all duration-200 text-center whitespace-nowrap"
                >
                  Learn More About {dataB.name}
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Additional CTAs */}
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <a
                href="/instant-quote"
                className="px-8 py-3 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-calculator-line mr-2"></i>
                Get Instant Quote
              </a>
              <a
                href="/quiz"
                className="px-8 py-3 bg-white border-2 border-slate-300 text-slate-900 font-semibold rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-questionnaire-line mr-2"></i>
                Take Our Quiz
              </a>
              <a
                href="/book-appointment"
                className="px-8 py-3 bg-white border-2 border-slate-300 text-slate-900 font-semibold rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-calendar-check-line mr-2"></i>
                Book Consultation
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ComparisonPage;